import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PagoProgramado, PaginatedResponse } from '../types';
import contractsService from '../services/contracts.service';
import paymentsService from '../services/payments.service';

const Pagos: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagosProgramados, setPagosProgramados] = useState<PagoProgramado[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showEfectuarModal, setShowEfectuarModal] = useState(false);
  const [selectedPago, setSelectedPago] = useState<PagoProgramado | null>(null);

  const isAlert = searchParams.get('alert') === 'true';

  const [limit, setLimit] = useState(20);

  const fetchPagos = useCallback(async (page = 1, searchTerm = search, status = statusFilter, currentLimit = limit) => {
    setLoading(true);
    try {
      const response: PaginatedResponse<PagoProgramado> = await contractsService.getPagosProgramados(page, currentLimit, searchTerm, status, isAlert);
      setPagosProgramados(response.pagosProgramados || []);
      setCurrentPage(page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching pagos programados:', error);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, isAlert, limit]);

  useEffect(() => {
    fetchPagos(1, search, statusFilter, limit);
  }, [fetchPagos, limit]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPagos(1, search, statusFilter, limit);
  };




  const clearAlert = () => {
    setSearchParams({});
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este pago programado?')) {
      try {
        await contractsService.deletePagoProgramado(id);
        fetchPagos(currentPage);
      } catch (error) {
        console.error('Error deleting pago programado:', error);
        alert('Error al eliminar el pago programado');
      }
    }
  };

  const handleEfectuar = (pago: PagoProgramado) => {
    setSelectedPago(pago);
    setShowEfectuarModal(true);
  };

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pagos Programados</h1>
          <p className="mt-2 text-sm text-gray-700">
            Cronograma de pagos proyectados ({total} registros)
          </p>
          {isAlert && (
            <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Filtrado por: Próximos a vencer o vencidos
              <button
                onClick={clearAlert}
                className="ml-2 inline-flex items-center p-0.5 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/pagos/nuevo"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Agregar Pago Programado
          </Link>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Buscar por cliente, referencia..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-48">
             <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="pagado">Pagado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            Buscar
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente/Contrato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha Programada
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Monto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagosProgramados.map((pago) => (
              <tr key={pago.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {pago.contrato?.cliente?.nombre}
                    </div>
                    <div className="text-sm text-gray-500">
                      {pago.contrato?.numeroContrato}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(pago.fecha).toLocaleDateString('es-CO')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="text-sm font-medium text-blue-600">
                    {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(pago.monto)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      pago.estado === 'pagado'
                        ? 'bg-green-100 text-green-800'
                        : pago.estado === 'cancelado'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {pago.estado}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {pago.descripcion || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  {pago.estado === 'pendiente' && (
                    <button
                      onClick={() => handleEfectuar(pago)}
                      className="text-green-600 hover:text-green-900 flex items-center"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Efectuar
                    </button>
                  )}
                  <Link
                    to={`/pagos/${pago.id}/editar`}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Editar
                  </Link>
                  <button
                    onClick={() => handleDelete(pago.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagosProgramados.length === 0 && !loading && (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay pagos programados</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comienza registrando un nuevo pago programado.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => fetchPagos(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => fetchPagos(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{(currentPage - 1) * limit + 1}</span> a <span className="font-medium">{Math.min(currentPage * limit, total)}</span> de{' '}
              <span className="font-medium">{total}</span> resultados
            </p>
            <select
              value={limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setLimit(newLimit);
                fetchPagos(1);
              }}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value={10}>10 por página</option>
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => fetchPagos(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Anterior</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum = i + 1;
                if (totalPages > 7) {
                  if (currentPage > 4) {
                    pageNum = currentPage - 3 + i;
                  }
                  if (pageNum > totalPages) {
                    pageNum = totalPages - (6 - i);
                  }
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => fetchPagos(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === pageNum
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => fetchPagos(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Siguiente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Modal para efectuar pago */}
      {showEfectuarModal && selectedPago && (
        <EfectuarPagoModal
          pago={selectedPago}
          onClose={() => {
            setShowEfectuarModal(false);
            setSelectedPago(null);
          }}
          onSuccess={() => {
            fetchPagos(currentPage);
            setShowEfectuarModal(false);
            setSelectedPago(null);
          }}
        />
      )}
    </div>
  );
};

// Componente Modal para efectuar pago
const EfectuarPagoModal: React.FC<{
  pago: PagoProgramado;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ pago, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [montoFormatted, setMontoFormatted] = useState('');
  const [montoBrutoFormatted, setMontoBrutoFormatted] = useState('');
  const [montoNetoFormatted, setMontoNetoFormatted] = useState('');
  const [retencionCalculada, setRetencionCalculada] = useState({ total: 0, porcentaje: 0 });
  const [formData, setFormData] = useState({
    fechaPago: new Date().toISOString().split('T')[0],
    monto: pago.monto,
    montoBruto: pago.monto,
    montoNeto: 0,
    detalleRetenciones: '',
    metodoPago: 'transferencia',
    referencia: '',
    observaciones: '',
  });

  // Inicializar el monto formateado
  useEffect(() => {
    const formatNumber = (value: string): string => {
      const cleanValue = value.replace(/[^\d.]/g, '');
      if (!cleanValue) return '';
      const parts = cleanValue.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    };
    
    setMontoFormatted(formatNumber(pago.monto.toString()));
    setMontoBrutoFormatted(formatNumber(pago.monto.toString()));
  }, [pago.monto]);

  // Calcular retención cuando cambian bruto o neto
  useEffect(() => {
    const bruto = parseFormattedNumber(montoBrutoFormatted);
    const neto = parseFormattedNumber(montoNetoFormatted);
    
    if (bruto > 0 && neto > 0 && neto <= bruto) {
      const retencion = bruto - neto;
      const porcentaje = (retencion / bruto) * 100;
      setRetencionCalculada({ total: retencion, porcentaje });
    } else {
      setRetencionCalculada({ total: 0, porcentaje: 0 });
    }
  }, [montoBrutoFormatted, montoNetoFormatted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Asegurar que el monto esté actualizado con el último valor formateado
      const finalMonto = parseFormattedNumber(montoFormatted);
      
      const finalMontoBruto = parseFormattedNumber(montoBrutoFormatted);
      const finalMontoNeto = parseFormattedNumber(montoNetoFormatted);
      
      await paymentsService.efectuarPagoProgramado(pago.id, {
        ...formData,
        monto: finalMonto,
        montoBruto: finalMontoBruto > 0 ? finalMontoBruto : undefined,
        montoNeto: finalMontoNeto > 0 ? finalMontoNeto : undefined,
        fechaPago: new Date(formData.fechaPago).toISOString(),
      });
      onSuccess();
    } catch (error) {
      console.error('Error efectuando pago:', error);
      alert('Error al efectuar el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'monto' ? parseFloat(value) || 0 : value
    }));
  };

  // Funciones para formateo de números
  const formatNumber = (value: string): string => {
    // Remover caracteres no numéricos excepto punto decimal
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    // Si está vacío, retornar vacío
    if (!cleanValue) return '';
    
    // Separar parte entera y decimal
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Formatear parte entera con separadores de miles
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Reconstruir el número
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  const parseFormattedNumber = (formattedValue: string): number => {
    // Remover separadores de miles y convertir a número
    const cleanValue = formattedValue.replace(/,/g, '');
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };


  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Efectuar Pago Programado
          </h3>
          
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <p className="text-sm text-gray-600">
              <strong>Cliente:</strong> {pago.contrato?.cliente?.nombre}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Contrato:</strong> {pago.contrato?.numeroContrato}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Monto programado:</strong> ${pago.monto.toLocaleString('es-CO')}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Fecha programada:</strong> {new Date(pago.fecha).toLocaleDateString('es-CO')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago Real *
              </label>
              <input
                type="date"
                name="fechaPago"
                required
                value={formData.fechaPago}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto Bruto (Factura) *
              </label>
              <input
                type="text"
                name="montoBruto"
                required
                value={montoBrutoFormatted}
                onChange={(e) => {
                  const formattedValue = formatNumber(e.target.value);
                  setMontoBrutoFormatted(formattedValue);
                  setFormData(prev => ({ ...prev, montoBruto: parseFormattedNumber(formattedValue) }));
                }}
                onBlur={() => setMontoBrutoFormatted(formatNumber(montoBrutoFormatted))}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Valor total de la factura</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto Neto Recibido *
              </label>
              <input
                type="text"
                name="montoNeto"
                required
                value={montoNetoFormatted}
                onChange={(e) => {
                  const formattedValue = formatNumber(e.target.value);
                  setMontoNetoFormatted(formattedValue);
                  setFormData(prev => ({ ...prev, montoNeto: parseFormattedNumber(formattedValue) }));
                }}
                onBlur={() => setMontoNetoFormatted(formatNumber(montoNetoFormatted))}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Valor que realmente recibiste</p>
            </div>

            {retencionCalculada.total > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm font-medium text-yellow-800">
                  Retención Total: ${retencionCalculada.total.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {retencionCalculada.porcentaje.toFixed(2)}% del monto bruto
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detalle de Retenciones (Opcional)
              </label>
              <textarea
                name="detalleRetenciones"
                rows={3}
                value={formData.detalleRetenciones}
                onChange={(e) => setFormData(prev => ({ ...prev, detalleRetenciones: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Ej: IVA: $159.664, Estampilla Prohospital: $20.000, Retención Fuente: $60.000"
              />
              <p className="text-xs text-gray-500 mt-1">Describe los descuentos aplicados</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Método de Pago *
              </label>
              <select
                name="metodoPago"
                required
                value={formData.metodoPago}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="transferencia">Transferencia Bancaria</option>
                <option value="cheque">Cheque</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta de Crédito</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referencia/Comprobante
              </label>
              <input
                type="text"
                name="referencia"
                value={formData.referencia}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Número de referencia"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observaciones
              </label>
              <textarea
                name="observaciones"
                rows={3}
                value={formData.observaciones}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Observaciones del pago"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Procesando...' : 'Efectuar Pago'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Pagos;